import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  debounce,
  fetchData,
  formatNumber,
  toasterrormsg,
  useMenuIdPermissions,
  getEncryptedRoute,
} from '../../../reusable'
import { KTIcon } from '../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import DataTable from 'react-data-table-component'
import Loader from '../components/Loader'
import { Tr } from '../../../_metronic/i18n/Tr'
import { DynamicChart, TooltipField } from '../dashboard/DashboardChartDynamic'

const CHART_TOOLTIP_FIELDS: TooltipField[] = [
  { key: 'totalSpend', label: 'Total Spent', format: 'number' },
  { key: 'totalAds', label: 'Total Ads', format: 'raw' },
  { key: 'activeAds', label: 'Active Ads', format: 'raw' },
  { key: 'completedAds', label: 'Completed Ads', format: 'raw' },
]

const VendorSpendInsights: FC = () => {
  const t = Tr()
  const { menuId } = useMenuIdPermissions()

  // View mode
  const [viewMode, setViewMode] = useState<'list' | 'chart'>('list')

  // List state
  const [reportList, setReportList] = useState<any[]>([])
  const [inputValue, setInputValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const [sortField, setSortField] = useState('')
  const [sortOrder, setSortOrder] = useState('')
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [focus, setFocus] = useState('')

  // Chart state
  const [chartData, setChartData] = useState<{ name: string; data: Record<string, any>[] }[]>([])
  const [chartCategories, setChartCategories] = useState<string[]>([])
  const [chartTotalSpent, setChartTotalSpent] = useState('0')
  const [chartLength, setChartLength] = useState('0')

  const fetchVendorSpendReport = async () => {
    try {
      if (!isDataLoaded) setIsLoading(true)
      const param = {
        limit,
        offset,
        term: searchTerm.trim(),
        sortBy: sortField,
        sort: sortOrder,
      }
      const response = await fetchData('admin/ads/perspendbyvendor/report', param)
      if (response.status == '200') {
        const data = response?.data ?? {}
        setTotalRecords(data.totalRecord || 0)
        setReportList(data.list || [])
        setIsDataLoaded(true)
        buildChartFromList(data.list || [])
      } else {
        toasterrormsg(response?.message || 'An error occurred')
        setReportList([])
        setTotalRecords(0)
      }
    } catch (error) {
      console.error('Error fetching vendor spend report:', error)
      toasterrormsg('Failed to fetch vendor spend report')
      setReportList([])
      setTotalRecords(0)
    } finally {
      setIsDataLoaded(true)
      setIsLoading(false)
    }
  }

  const buildChartFromList = (list: any[]) => {
    if (!list || list.length === 0) {
      setChartLength('0')
      setChartData([])
      setChartCategories([])
      setChartTotalSpent('0')
      return
    }

    const categories = list.map((v: any) => v.fullName || 'Unknown')
    const dataPoints = list.map((v: any) => ({
      x: v.fullName || 'Unknown',
      y: parseFloat(v.totalSpend || 0),
      totalSpend: parseFloat(v.totalSpend || 0),
      totalAds: v.totalAds || 0,
      activeAds: v.activeAds || 0,
      completedAds: v.completedAds || 0,
    }))

    const total = list.reduce((acc: number, v: any) => acc + parseFloat(v.totalSpend || 0), 0)

    setChartCategories(categories)
    setChartData([{ name: '', data: dataPoints }])
    setChartTotalSpent(String(total))
    setChartLength(String(categories.length))
  }

  // Search
  const adSearch = (text: string, isfoc: any) => {
    setInputValue(text)
    setFocus(isfoc)
    debouncedSearch(text)
  }

  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearchTerm(value)
      setOffset(0)
    }, 500)
  ).current

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [])

  // Sort
  const handleSort = (column: any, sortDirection: string) => {
    try {
      const fieldName = column.selector ? String(column.selector).split('.').pop() : ''
      setSortField(fieldName || '')
      setSortOrder(sortDirection === 'asc' ? 'asc' : 'desc')
    } catch (error) {
      console.error('Error in sorting:', error)
    }
  }

  // Pagination
  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / limit)
    if (newPage === 1) setOffset(0)
    else if (newPage === totalPages) setOffset((totalPages - 1) * limit)
    else setOffset((newPage - 1) * limit)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage)
    setOffset(0)
  }

  // Table Search Header
  const CustomHeader = (props: any) => {
    const inputRef: any = useRef(null)
    const handleChange = async (e: any) => {
      await props.customSearch(e, 1)
    }
    useEffect(() => {
      if (inputRef.current && props.isfoc === 1) inputRef.current.focus()
    }, [props.isfoc])

    return (
      <div className='d-flex justify-content-between w-100 my-5'>
        <div className='d-flex align-items-center position-relative my-1'>
          <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
          <input
            type='text'
            data-kt-user-table-filter='search'
            className='form-control form-control-solid w-250px ps-14'
            placeholder={t('Search Vendor')}
            value={props.search}
            onChange={(e: any) => handleChange(e.target.value)}
            ref={inputRef}
          />
        </div>
      </div>
    )
  }

  // Columns
  const columns = useMemo(
    () => [
      {
        name: t('Vendor'),
        cell: (row: any) => (
          <div className='d-flex align-items-center gap-3 my-3'>
            
            <div>
              <div className='fw-bold fs-5'>{row.fullName || '-'}</div>
            </div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.fullName,
        width: '250px',
      },
       {
        name: t('Vendor Category'),
        cell: (row: any) => (
          <div className='d-flex align-items-center gap-3 my-3'>
            
            <div>
              <div className='fw-bold fs-5'>{row.en_name || '-'}</div>
            </div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.v,
        width: '250px',
      },
      {
        name: t('Total Ads'),
        cell: (row: any) => (
          <div className='my-3'>
            <span className='vs-badge vs-badge--ads'>
              <KTIcon iconName='abstract-26' className='fs-4' />
              {row.totalAds || 0}
            </span>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.totalAds,
        width: '140px',
      },
      {
        name: t('Total Spent'),
        cell: (row: any) => (
          <div className='my-3'>
            <span className='fw-bold fs-5'>
              {formatNumber(parseFloat(row.totalSpend || 0) , true)}
            </span>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.totalSpend,
        width: '180px',
      },
      {
        name: t('Active Ads'),
        cell: (row: any) => (
          <div className='my-3'>
            <span className='vs-badge vs-badge--active'>
              {row.activeAds || 0}
            </span>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.activeAds,
        width: '130px',
      },
      {
        name: t('Completed Ads'),
        cell: (row: any) => (
          <div className='my-3'>
            <span className='vs-badge vs-badge--completed'>
              {row.completedAds || 0}
            </span>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.completedAds,
        width: '140px',
      },
      {
        name: t('Action'),
        cell: (row: any) => (
          <div className='my-3'>
            <Link
              to={getEncryptedRoute('../vendoradslist/', {
                menuId,
                vendorId: row.vendorId,
                vendorName: row.fullName,
              })}
              className='btn btn-light-primary btn-sm d-flex align-items-center gap-1 px-3 py-2 rounded-pill'
            >
              <KTIcon iconName='eye' className='fs-4' iconType='solid' />
              <span className='fs-7'>{t('View All Ads')}</span>
            </Link>
          </div>
        ),
        sortable: false,
        width: '160px',
      },
    ],
    [t, menuId]
  )

  useEffect(() => {
    fetchVendorSpendReport()
  }, [limit, offset, sortField, sortOrder, searchTerm])

  useEffect(() => {
    document.title = 'Insayr | Vendor Spend Insights'
  }, [])

  return (
    <>
      <ToastContainer />
      {isLoading && <Loader />}

      {/* Info Banner */}
      <div className='vs-info-banner'>
        <KTIcon iconName='chart-line' className='fs-1 text-primary flex-shrink-0' />
        <div>
          <div className='fw-bold fs-6 text-primary mb-1'>{t('Vendor Spend Insights')}</div>
          <p className='text-gray-700 fs-7 mb-0'>
            {t('Overview of how much each vendor has spent on advertisements. Click "View All Ads" to see detailed ad listings.')}
          </p>
        </div>
      </div>

      {/* View Toggle */}
      <div className='d-flex justify-content-end mb-4'>
        <div className='vs-view-toggle'>
          <button
            className={`vs-view-toggle__btn ${viewMode === 'list' ? 'vs-view-toggle__btn--active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <KTIcon iconName='row-horizontal' className={`fs-4 ${viewMode === 'list' ? 'text-white' : 'text-muted'}`} />
            {t('List')}
          </button>
          <button
            className={`vs-view-toggle__btn ${viewMode === 'chart' ? 'vs-view-toggle__btn--active' : ''}`}
            onClick={() => setViewMode('chart')}
          >
            <KTIcon iconName='chart-simple' className={`fs-4 ${viewMode === 'chart' ? 'text-white' : 'text-muted'}`} />
            {t('Chart')}
          </button>
        </div>
      </div>

      {/* ── List View ── */}
      {viewMode === 'list' && (
        <DataTable
          columns={columns}
          data={reportList}
          pagination
          paginationServer
          onChangePage={handlePageChange}
          paginationTotalRows={totalRecords}
          paginationPerPage={limit}
          onChangeRowsPerPage={handleRowsPerPageChange}
          paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
          sortServer
          onSort={handleSort}
          subHeader
          subHeaderComponent={<CustomHeader search={inputValue} isfoc={focus} customSearch={adSearch} />}
          paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
          noDataComponent={
            <div className='vs-empty-state'>
              <KTIcon iconName='chart-line' className='fs-3x text-muted mb-3' />
              <div className='fw-bold fs-5 text-gray-600 mb-1'>{t('No Vendor Data')}</div>
              <p className='text-muted fs-7'>{t('No vendor spend reports found.')}</p>
            </div>
          }
        />
      )}

      {/* ── Chart View ── */}
      {viewMode === 'chart' && (
        <DynamicChart
          className='card-xl-stretch mb-5 mb-xl-8'
          title='Total Spent'
          totalValue={chartTotalSpent}
          categories={chartCategories}
          data={chartData}
          tooltipFields={CHART_TOOLTIP_FIELDS}
          length={chartLength}
          emptyMessage={t('No chart data available')}
        />
      )}
    </>
  )
}

export { VendorSpendInsights }
